import { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useDebounce } from 'usehooks-ts';
import AlertDialog from 'components/AlertDialog';
import Table from 'components/Table';
import PageContainer from 'components/PageContainer';
import { numberToCurrencyString, dateToLabel } from 'further-ui/utils';
import { Api, Status } from 'further-types/eis-wizard';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import TaxReliefElegibilitySwitch from './TaxReliefElegibilitySwitch';
import { EIS_ADD, EIS } from 'adminConstants/routes';
import { useGetRole } from 'hooks/ui/useGetRole';
import ProcessCompletedChip from './ProcessCompletedChip';
import ListCertificateFilters from './ListCertificateFilters';
import RowActions from './RowActions';
import { InfoOutlined } from '@mui/icons-material';
import useEisTasklist from 'hooks/data/eisWizard/useEisTasklist';
import useKiFundCertificateBatch from 'hooks/data/kiFundCertificate/useKiFundCertificateBatch';
import { useEisWizard } from 'hooks/data/eisWizard/useEisWizard';
import PageContent from 'components/PageContent';
import PageFilters from 'components/PageFilters';
import { BackgroundExportTypes } from 'further-types/background-exports';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

const eisTooltipText =
  'Please toggle here if a subscription loses its tax relief status during its hold period (e.g. the business starts a non-EIS qualifying trade). Do not toggle S/EIS eligibility here if the subscription has lost tax relief as result of an early exit. Tax status on exit is selected in the Exits function, at the point of processing an exit.';

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'S/EIS Tasklist', link: EIS },
];

type TaskListItem = Api.GetEisWizardTaskListResponse['results'][number];

const EisWizard: React.FC = () => {
  const deleteDisclosure = useDisclosure<{
    eisWizard?: { id: string };
    kiFundCertificatesBatchId?: string;
    createdWithoutCertificates?: boolean;
  }>();
  const { firmId: usersFirmId, isSuperAdmin } = useGetRole();
  const [canCreateSeis] = useHasPermissions([RolePermission.CreateSeis]);

  const pagination = usePagination({
    orderBy: 'allocationDate',
    order: 'desc',
    id: 'certificates-list',
  });

  const { filters, handleChangeFilters } = useFilters('certificates-list', {
    onFiltersChange: pagination.toFirstPage,
    defaultFilters: {
      firmId: usersFirmId,
      search: '',
      status: null,
    },
  });

  const [search, setSearch] = useState(filters.search);
  const debouncedSearch = useDebounce(search, 300);
  useEffect(() => {
    handleChangeFilters({ search: debouncedSearch });
  }, [debouncedSearch]);

  const mapUiStatusToApiStatus = {
    all: '',
    [Status.Complete]: Status.Complete,
    [Status.NotComplete]: Status.NotComplete,
    [Status.NA]: Status.NA,
  };

  const {
    tasklist: { data, isFetching },
  } = useEisTasklist({
    rowsPerPage: pagination.rowsPerPage,
    page: pagination.page,
    order: pagination.order,
    orderBy: pagination.orderBy,
    ...(filters.firmId ? { firmId: filters.firmId } : {}),
    search: debouncedSearch,
    //@ts-expect-error
    status: mapUiStatusToApiStatus[filters.status],
  });
  const { deleteCertificateBatch } = useKiFundCertificateBatch();
  const { deleteEisWizard } = useEisWizard();

  const columns = [
    {
      label: 'Company',
      key: 'companyName',
    },
    {
      label: 'Share class',
      key: 'shareClass',
    },
    {
      label: 'Firm',
      key: 'firmName',
    },
    {
      label: 'Allocation date',
      key: 'allocationDate',
      render: (elm: TaskListItem) => dateToLabel(elm?.allocationDate),
    },
    {
      label: 'Tranche(s)',
      key: 'fundNames',
      sort: false,
      render: (elm: TaskListItem) => {
        if (elm.fundNames.length <= 2) {
          return elm.fundNames.join(', ');
        } else {
          return `${elm.fundNames.slice(0, 2).join(', ')} and ${
            elm.fundNames.length - 2
          } more`;
        }
      },
    },
    {
      label: 'Funds allocated',
      key: 'subscriptionAmount',
      sort: false,
      render: (elm: TaskListItem) =>
        numberToCurrencyString(elm.subscriptionAmount),
    },
    {
      label: 'Process completed?',
      key: 'eisWizard._id',
      render: (elm: TaskListItem) => (
        <ProcessCompletedChip tasklistItem={elm} />
      ),
    },
    {
      label: 'Date of completion',
      key: 'completionDate',
      render: (elm: TaskListItem) =>
        elm.completionDate ? dateToLabel(elm.completionDate, '-') : 'N/A',
    },
    {
      label: (
        <Box display="flex" alignItems="center" gap="4px">
          <span>S/EIS eligible?</span>
          <Tooltip title={eisTooltipText}>
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      key: 'taxReliefElegible',
      render: (elm: TaskListItem) =>
        elm.shareAllocationUploadId ? (
          <TaxReliefElegibilitySwitch
            value={elm.taxReliefElegible}
            processCompleted={
              (!!elm.eisWizard?.id && !elm.createdWithoutCertificates) ||
              elm.createdWithoutCertificates
            }
            shareAllocationUploadId={elm.shareAllocationUploadId}
            exitId={elm.exitId}
          />
        ) : (
          <>-</>
        ),
    },
    {
      label: 'Actions',
      key: '',
      sort: false,
      render: (elm: TaskListItem) => (
        <RowActions deleteDisclosure={deleteDisclosure} tasklistItem={elm} />
      ),
    },
  ];

  const handleConfirmDelete = async () => {
    if (deleteDisclosure.actionProps?.eisWizard?.id) {
      deleteEisWizard.mutate(deleteDisclosure.actionProps.eisWizard.id);
    } else if (deleteDisclosure.actionProps?.kiFundCertificatesBatchId) {
      deleteCertificateBatch.mutate(
        deleteDisclosure.actionProps.kiFundCertificatesBatchId,
      );
    }
    deleteDisclosure.onClose();
  };

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.EisTasklistExport,
      firmId: filters.firmId,
      params: {
        status: mapUiStatusToApiStatus[filters.status ?? ''] || undefined,
        search: debouncedSearch,
        order: pagination.order,
        orderBy: pagination.orderBy,
      },
    });
  };

  const createdWithoutCertificates =
    deleteDisclosure.actionProps?.createdWithoutCertificates;

  return (
    <PageContainer heading="S/EIS Tasklist" breadcrumbs={breadcrumbs}>
      <PageContent>
        <PageFilters
          pageId="certificates-list"
          buttons={[
            {
              link: EIS_ADD,
              label: 'Create S/EIS Certificates',
              visible: canCreateSeis,
            },
            {
              label: 'Export',
              onClick: handleExport,
              loading: isFetching || isExporting,
              disabled: isFetching || isExporting,
            },
          ]}
        >
          <ListCertificateFilters
            filters={filters}
            handleChangeFilters={handleChangeFilters}
            search={search}
            setSearch={setSearch}
          />
        </PageFilters>

        <Table
          columns={
            isSuperAdmin
              ? columns
              : columns.filter((column) => column.key !== 'firmName')
          }
          onRequestSort={pagination.handleRequestSort}
          order={pagination.order}
          orderBy={pagination.orderBy}
          tablebody={data?.results || []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          pagination={true}
          count={data?.total || 0}
          loading={isFetching}
          variant="nohover"
        />
      </PageContent>
      <AlertDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={handleConfirmDelete}
        title={
          createdWithoutCertificates
            ? 'Are you sure you wish to reset this allocation’s S/EIS status?'
            : 'Are you sure you wish to delete these S/EIS certificates?'
        }
        content={
          <Typography
            sx={{ ...(createdWithoutCertificates && { margin: '0px 24px' }) }}
          >
            {createdWithoutCertificates
              ? 'This cannot be undone and previously entered information will be lost.'
              : 'This cannot be undone and all information relating to the S/EIS certificates will be lost.'}
          </Typography>
        }
        btnLabels={{
          cancel: 'Go back',
          confirm: createdWithoutCertificates
            ? 'Reset status'
            : 'Delete certificates',
        }}
      />
    </PageContainer>
  );
};

export default EisWizard;
